// ─────────────────────────────────────────────────────────────────────
// Brand colors (Paramètres généraux → Couleurs).
//
// The admin picks 2 colors; everything else (hover, soft tints, tinted
// surfaces/borders, dark-mode variants, readable text color) is derived
// here and written as --brand-* variables on <html>. _tokens.scss reads
// them with the default theme as fallback, so no color = default theme.
//
// The computed variables are cached in localStorage and re-applied by an
// inline script in public/index.html before the bundle loads (no flash of
// the default color). Keep this file identical in admin / client / livreur.
// ─────────────────────────────────────────────────────────────────────

export const DEFAULT_PRIMARY = "#FF5500";
export const DEFAULT_SECONDARY = "#1C1917";
// White text is drawn on both colors (must match SettingsController::contrastRule)
export const MIN_CONTRAST_PRIMARY = 3;
export const MIN_CONTRAST_SECONDARY = 4.5;

const STORAGE_KEY = "brand-theme";
const DARK_SURFACE = "#1C1917"; // --ui-surface in dark mode

type RGB = [number, number, number];
const WHITE: RGB = [255, 255, 255];
const BLACK: RGB = [0, 0, 0];

const BRAND_VARS = [
  "--brand-accent",
  "--brand-accent-rgb",
  "--brand-accent-strong",
  "--brand-accent-text",
  "--brand-accent-text-dark",
  "--brand-on-accent",
  "--brand-focus-outline-dark",
  "--brand-surface-sunken",
  "--brand-surface-hover",
  "--brand-border",
  "--brand-border-strong",
  "--brand-ink",
  "--brand-ink-hover",
  "--brand-on-ink",
  "--brand-ink-dark",
  "--brand-ink-dark-hover",
  "--brand-on-ink-dark",
];

/** "#f50", "f50", "#FF5500" → "#FF5500"; anything else → null */
export function normalizeHex(value: unknown): string | null {
  let v = String(value ?? "").trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(v)) v = v.replace(/./g, "$&$&");
  return /^[0-9a-f]{6}$/i.test(v) ? `#${v.toUpperCase()}` : null;
}

function toRgb(hex: string): RGB {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function toHex(rgb: RGB): string {
  return `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}
/** t = share of `b` in the mix */
function mix(a: RGB, b: RGB, t: number): RGB {
  return [0, 1, 2].map((i) => a[i] + (b[i] - a[i]) * t) as RGB;
}
function luminance([r, g, b]: RGB): number {
  const lin = (c: number) => ((c /= 255) <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
function ratio(a: RGB, b: RGB): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** WCAG contrast ratio between a color and white text */
export function contrastWithWhite(hex: string): number {
  return ratio(toRgb(hex), WHITE);
}

/** Moves `from` toward `toward` (same hue) until it reaches `min` contrast against `bg` */
function shiftToContrast(from: RGB, toward: RGB, bg: RGB, min: number): RGB {
  for (let t = 0; t <= 1; t += 0.02) {
    const c = mix(from, toward, t);
    if (ratio(c, bg) >= min) return c;
  }
  return toward;
}

/** Darkest-needed version of a color so white text stays readable on it */
export function fixContrast(hex: string, min: number): string {
  return toHex(shiftToContrast(toRgb(hex), BLACK, WHITE, min));
}

/** The --brand-* variables for these colors (empty object = default theme) */
export function brandVars(primary: unknown, secondary: unknown): Record<string, string> {
  const vars: Record<string, string> = {};
  const p = normalizeHex(primary);
  const s = normalizeHex(secondary);

  if (p && p !== DEFAULT_PRIMARY) {
    const rgb = toRgb(p);
    const textDark = shiftToContrast(mix(rgb, WHITE, 0.35), WHITE, toRgb(DARK_SURFACE), 4.5);
    vars["--brand-accent"] = p;
    vars["--brand-accent-rgb"] = rgb.join(", ");
    vars["--brand-accent-strong"] = toHex(mix(rgb, BLACK, 0.15));
    vars["--brand-accent-text"] = toHex(shiftToContrast(rgb, BLACK, WHITE, MIN_CONTRAST_PRIMARY));
    vars["--brand-accent-text-dark"] = toHex(textDark);
    vars["--brand-on-accent"] = ratio(rgb, WHITE) >= MIN_CONTRAST_PRIMARY ? "#FFFFFF" : "#1C1917";
    vars["--brand-focus-outline-dark"] = `rgba(${textDark.map(Math.round).join(", ")}, 0.8)`;
    // Light-mode surfaces are tinted with the primary (same ratios as the default theme)
    vars["--brand-surface-sunken"] = toHex(mix(WHITE, rgb, 0.065));
    vars["--brand-surface-hover"] = toHex(mix(WHITE, rgb, 0.12));
    vars["--brand-border"] = toHex(mix([249, 249, 249], rgb, 0.2));
    vars["--brand-border-strong"] = toHex(mix([227, 227, 227], rgb, 0.17));
  }

  if (s && s !== DEFAULT_SECONDARY) {
    const rgb = toRgb(s);
    // Dark mode keeps the default "light button" look, tinted with the secondary
    const inkDark = mix(rgb, WHITE, 0.92);
    vars["--brand-ink"] = s;
    vars["--brand-ink-hover"] = toHex(mix(rgb, BLACK, 0.25));
    vars["--brand-on-ink"] = ratio(rgb, WHITE) >= MIN_CONTRAST_SECONDARY ? "#FFFFFF" : "#1C1917";
    vars["--brand-ink-dark"] = toHex(inkDark);
    vars["--brand-ink-dark-hover"] = toHex(mix(rgb, WHITE, 0.95));
    vars["--brand-on-ink-dark"] = ratio(rgb, inkDark) >= 4.5 ? s : "#0F0E0D";
  }

  return vars;
}

/**
 * Applies the colors to the whole app. `persist: false` is used by the
 * settings page for the live preview (not cached until saved).
 */
export function applyBrandTheme(primary: unknown, secondary: unknown, persist = true): void {
  const vars = brandVars(primary, secondary);
  const style = document.documentElement.style;
  BRAND_VARS.forEach((name) => (vars[name] ? style.setProperty(name, vars[name]) : style.removeProperty(name)));
  if (!persist) return;
  try {
    if (Object.keys(vars).length) localStorage.setItem(STORAGE_KEY, JSON.stringify(vars));
    else localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // storage unavailable (private mode…) — the theme still applies for this visit
  }
}
